import React from "reactn";
import { $, _ } from "js/vendor";
import moment from "moment";
import { API_ERROR_CODE } from "common/constants";
import log from "js/core/logger";
import { ds } from "js/core/models/dataService";
import * as geom from "js/core/utilities/geom";
import isConnected from "js/core/utilities/isConnected";
import { SVGRectElement } from "js/elements/base/SVGElement";
import { BaseElement } from "js/elements/base/BaseElement";
import Api from "js/core/api";
import { ElementTextBlockPositionType } from "common/constants";
import { isElectron } from "js/config";

export class Video extends BaseElement {
    get isInteractive() {
        return true;
    }

    get interactiveAction() {
        return {
            type: "video",
        };
    }

    get showDefaultOverlay() {
        return _.isEmpty(this.model.videoId) && _.isEmpty(this.model.videoAssetId);
    }

    get videoUrl() {
        if (this.model.videoId) {
            if (this.model.videoId.startsWith("http")) {
                return this.model.videoId;
            } else {
                return "https://www.youtube.com/watch?v=" + this.model.videoId; // migration for old youtube video ids
            }
        } else {
            return null;
        }
    }

    get videoType() {
        if (this.model.videoAssetId) return "upload";
        if (!this.videoUrl) return "none";
        if (this.videoUrl.contains("vimeo")) return "vimeo";
        if (this.videoUrl.contains("youtube") || this.videoUrl.contains("youtu.be")) return "youtube";
        return "none";
    }

    get isAvailableOffline() {
        return isElectron && this.videoType === "upload";
    }

    get startTime() {
        return this.model.startTime || 0;
    }

    get autoPlay() {
        return this.canvas?.isPlayback ? (this.model?.autoPlay ? true : false) : false;
    }

    get muted() {
        return this.model?.muted || false;
    }

    get isCacheValid() {
        if (!this.cachedHTML) {
            return false;
        }

        if (this.cacheValidTill && this.cacheValidTill < moment().valueOf()) {
            return false;
        }

        if (this.cachedVideoId !== this.model.videoId ||
            this.cachedVideoAssetId !== this.model.videoAssetId ||
            this.cachedStartTime !== this.model.startTime ||
            this.cachedMuted !== this.model.muted) {
            return false;
        }

        return true;
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRefreshElement() {
        return true;
    }

    saveCache(html, cacheTTLMs = null) {
        this.cachedHTML = html;
        this.cacheValidTill = cacheTTLMs ? moment().valueOf() + cacheTTLMs : null;
        this.cachedVideoId = this.model.videoId;
        this.cachedVideoAssetId = this.model.videoAssetId;
        this.cachedStartTime = this.model.startTime;
        this.cachedMuted = this.model.muted;
    }

    getCanvasMargins() {
        let fullBleed = this.model.fullBleed;
        if (this.canvas.model.layout.showElementAttribution || this.canvas.model.layout.elementTextBlockPosition == ElementTextBlockPositionType.INLINE) {
            fullBleed = false;
        }
        if (fullBleed) {
            if (this.canvas.layouter.showFooter) {
                return { left: 0, top: 0, bottom: 10, right: 0 };
            } else {
                return { left: 0, top: 0, bottom: 0, right: 0 };
            }
        } else {
            return { left: 50, top: 50, bottom: 50, right: 50 };
        }
    }

    getOriginalVideoSize() {
        return this.originalVideoSize || null;
    }

    async _load() {
        if (window.isRenderer) {
            this.labelText = "Video";
            return;
        }

        this.labelText = "";

        if (this.videoType === "none") {
            return;
        }

        if (!this.isAvailableOffline && !isConnected.connected) {
            this.labelText = "Video is not available offline";
            return;
        }

        if (this.isCacheValid) {
            return;
        }

        if (this.videoType === "upload") {
            try {
                const asset = await ds.assets.getAssetById(this.model.videoAssetId, "video");
                const url = await asset.getURL();
                this.originalVideoSize = new geom.Size(800, 450); // Default it to 16:9 size
                const html = `<video width="100%" height="100%" controls disablePictureInPicture controlsList="nodownload" ${this.autoPlay ? "autoplay" : ""}><source src="${url}#t=${this.startTime}" type="video/mp4"></source><video>`;

                // Briefly create a dom element so we can get the video metadata
                const $video = $(html);
                $video.on("loadedmetadata", event => {
                    const { videoWidth, videoHeight } = $video[0];
                    this.originalVideoSize = new geom.Size(videoWidth, videoHeight);
                    $video.remove();
                });

                this.saveCache(html, 5 * 60 * 1000);
                return;
            } catch (err) {
                log.error(err);
                this.labelText = "Video not found";
            }
        }

        const oembedUrl = this.videoType === "vimeo"
            ? `https://www.vimeo.com/api/oembed.json?url=${encodeURIComponent(this.videoUrl)}&autoplay=${this.autoPlay ? 1 : 0}&byline=false&portrait=false&title=false&muted=${this.muted ? 1 : 0}`
            : `https://www.youtube.com/oembed?url=${encodeURIComponent(this.videoUrl)}&format=json`;

        try {
            const response = await Api.oembed.post({ url: oembedUrl });
            let { html, width, height } = response;
            this.originalVideoSize = new geom.Size(width, height);
            if (this.videoType === "youtube") {
                // youtube doesn't pass through the autoplay or start properties so fix them now
                html = html.replace("feature=oembed", `feature=oembed&rel=0&modestbranding=1&mute=${this.muted ? 1 : 0}&autoplay=${this.autoPlay.toString()}&start=${this.startTime}`);
            } else {
                html = html.replace('" width', `#t=${this.canvas.isPlayback ? this.startTime : 0}s" width`);
            }

            // Setting with and height of the iframe to 100%
            html = html.replace(/height=[^s]+/, "");
            html = html.replace(/width=[^s]+/, `width=\"100%\" height=\"100%\"`);

            this.saveCache(html, null);
        } catch (err) {
            log.error(err);
            if (err.code && err.code === API_ERROR_CODE.FORBIDDEN) {
                this.labelText = "The permissions on the video do not allow oembed";
            } else {
                this.labelText = "Could not load video";
            }
        }
    }

    _build() {
        this.background = this.addElement("background", () => SVGRectElement);
        this.background.layer = -1;
    }

    _calcProps(props, options) {
        const { size, children } = props;

        const backgroundProps = children.setProps(this.background, size, {
            styles: { fillColor: "black" }
        });
        backgroundProps.bounds = new geom.Rect(0, 0, size);

        return { size };
    }

    handlePlaybackClick = event => {
        if (this.canvas.isPlayback) {
            if (this.canvas.options.advanceToSlide) {
                this.canvas.options.advanceToSlide(1);
            } else if (this.canvas.playerView) {
                this.canvas.playerView.$container.trigger(event);
                this.canvas.playerView.goNextSlide(true);
            }
        }
    }

    renderChildren(props, transition) {
        const children = super.renderChildren(props, transition);

        if (this.isCacheValid && this.canvas.isCurrentCanvas) {
            const style = { position: "absolute", width: "100%", height: "100%", pointerEvents: "all" };
            // Removing the ugly white borders for vimeo embeds
            if (this.videoType === "vimeo") {
                style.top = "-2px";
                style.left = "-2px";
                style.width = "calc(100% + 2px)";
                style.height = "calc(100% + 2px)";
            }
            children.push(<div key="video" style={style} dangerouslySetInnerHTML={{ __html: this.cachedHTML }} />);

            if (this.canvas.isPlayback && !this.options.noPlaybackArrow) {
                children.push(<div
                    onClick={this.handlePlaybackClick}
                    className="video-clickshield" style={{
                        width: props.bounds.width * .333,
                        height: props.bounds.height - 100,
                        left: props.bounds.width - props.bounds.width * .333,
                        top: 0,
                    }}>
                    <i className="micon">arrow_forward</i>
                </div>
                );
            }
        } else if (this.labelText) {
            children.push(<div key="video" style={{ position: "absolute", width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}><h1 style={{ fontSize: "42px", color: "#ffffff" }}>{this.labelText}</h1></div>);
        }

        return children;
    }

    registerAnimations(animationManager) {
        animationManager.showAnimationProperties = false;
    }

    _prepareToShowElement() {
        if (this.isAvailableOffline || isConnected.connected) {
            _.defer(() => {
                this.canvas.refreshCanvas();
            });
        }
    }

    _stopElement() {
        if (this.canvas.isPlayback && this.canvas.playerView && this.canvas.playerView.closing) return; // don't fire off async refreshCanvas if this was called because we are closing the player

        this.refreshElement();
    }
}

export const elements = {
    Video
};
